import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useLanguage, languages } from '../../context/LanguageContext';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// Reduced motion variants for better performance
const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.4 }
};

export default function HeroSection() {
  const sectionRef = useRef(null);
  const { t, language, changeLanguage } = useLanguage();
  
  const currentLang = languages.find(l => l.code === language);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"]
  });
  
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 50]);

  const scrollToSection = () => {
    const element = document.querySelector('#leistungen');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      ref={sectionRef}
      id="hero"
      className="min-h-screen bg-white relative px-6 md:px-12 lg:px-16 pt-8 pb-8 flex flex-col"
      data-testid="hero-section"
    >
      {/* Top Bar */}
      <div className="flex justify-between items-center mb-auto">
        {/* Logo - Easyweb with capital E */}
        <div className="text-[#0a0a0a] font-bold text-lg tracking-tight font-['Manrope']">
          Easyweb
        </div>

        {/* Center - Swiss Made */}
        <div className="hidden md:flex items-center gap-2 text-sm text-[#0a0a0a] font-medium">
          <span className="w-2 h-2 bg-[#0a0a0a] rounded-full"></span>
          <span>Swiss Made</span>
        </div>

        {/* Right - Navigation + Language */}
        <div className="hidden md:flex items-center gap-6">
          <nav className="flex items-center gap-6 text-sm text-[#0a0a0a] font-medium">
            <a href="#leistungen" className="hover:opacity-60 transition-opacity">{t('nav.leistungen')}</a>
            <a href="#projekte" className="hover:opacity-60 transition-opacity">{t('nav.projekte')}</a>
            <a href="#team" className="hover:opacity-60 transition-opacity">{t('nav.ueberUns')}</a>
            <a href="#kontakt" className="hover:opacity-60 transition-opacity">{t('nav.kontakt')}</a>
          </nav>
          
          {/* Language Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 text-sm text-[#0a0a0a] font-medium hover:opacity-60 transition-opacity">
              <span>{currentLang?.code.toUpperCase()}</span>
              <ChevronDown className="w-4 h-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white border border-[#0a0a0a]">
              {languages.map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`cursor-pointer ${language === lang.code ? 'font-bold' : ''}`}
                >
                  {lang.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Hero Content */}
      <div className="flex-1 flex items-center justify-center relative">
        
        {/* Image BEHIND the text (z-0) - BIGGER and shifted left on mobile */}
        <motion.div
          style={{ y: imageY, top: '5%' }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="absolute z-0 w-[280px] md:w-[380px] lg:w-[450px] left-[35%] md:left-1/2 -translate-x-1/2"
        >
          <div className="relative" style={{ transform: 'rotate(-5deg)' }}>
            {/* Grey background frame */}
            <div className="absolute -inset-4 bg-[#e5e5e5] -rotate-2 -z-10"></div>
            <img
              src="https://customer-assets.emergentagent.com/job_modern-easyweb/artifacts/nabtmse9_ChatGPT%20Image%201.%20Feb.%202026%2C%2020_14_29.png"
              alt="Webagentur Schweiz - Team easywebswiss.ch"
              className="w-full h-auto object-cover"
              width="450"
              height="600"
              loading="eager"
              fetchpriority="high"
            />
          </div>
        </motion.div>

        {/* Giant Text - EASYWEB on ONE LINE (z-10, in front of image) */}
        <div className="relative z-10 text-center">
          <div className="overflow-hidden">
            <motion.h1
              initial={{ y: "100%" }}
              animate={{ y: "0%" }}
              transition={{ delay: 0.3, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="text-[15vw] md:text-[14vw] lg:text-[13vw] leading-none font-bold text-[#0a0a0a] font-['Manrope'] tracking-[-0.04em]"
            >
              EASYWEB
            </motion.h1>
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.6 }}
        className="flex flex-col md:flex-row justify-between items-center md:items-end mt-auto gap-6 md:gap-0"
      >
        {/* Social Icons - Bottom Left */}
        <div className="flex items-center gap-3 order-2 md:order-1">
          <a 
            href="https://www.instagram.com/easywebswiss.ch/?hl=de" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="w-10 h-10 border border-[#0a0a0a] rounded-full flex items-center justify-center hover:bg-[#0a0a0a] hover:text-white transition-colors text-[#0a0a0a]"
            data-testid="social-instagram"
            aria-label="Instagram"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
          </a>
          <a 
            href="https://wa.me/41783202461" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="w-10 h-10 border border-[#0a0a0a] rounded-full flex items-center justify-center hover:bg-[#25D366] hover:border-[#25D366] hover:text-white transition-colors text-[#0a0a0a]"
            data-testid="social-whatsapp"
            aria-label="WhatsApp"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          </a>
          <a 
            href="#kontakt" 
            onClick={(e) => { e.preventDefault(); document.querySelector('#kontakt')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="w-10 h-10 border border-[#0a0a0a] rounded-full flex items-center justify-center hover:bg-[#0a0a0a] hover:text-white transition-colors text-[#0a0a0a]"
            data-testid="social-email"
            aria-label="Kontakt aufnehmen"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
          </a>
          <a 
            href="tel:+41783202461" 
            className="w-10 h-10 border border-[#0a0a0a] rounded-full flex items-center justify-center hover:bg-[#0a0a0a] hover:text-white transition-colors text-[#0a0a0a]"
            data-testid="social-phone"
            aria-label="Anrufen +41 78 320 24 61"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
          </a>
          <a 
            href="https://www.linkedin.com/company/easywebswiss/about/" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="w-10 h-10 border border-[#0a0a0a] rounded-full flex items-center justify-center hover:bg-[#0a0a0a] hover:text-white transition-colors text-[#0a0a0a]"
            data-testid="social-linkedin"
            aria-label="LinkedIn"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
          </a>
        </div>

        {/* Center - Subtitle */}
        <div className="text-center order-1 md:order-2">
          <p className="text-sm md:text-base text-[#0a0a0a] font-medium">
            Webagentur
          </p>
          <p className="text-sm md:text-base text-[#0a0a0a]">Designer</p>
          <p className="text-sm md:text-base text-[#0a0a0a]">{t('hero.swissExperts') || 'Schweizer Experten'}</p>
        </div>

        {/* Scroll Indicator - Bottom Right - Dark Black */}
        <div 
          className="cursor-pointer text-sm text-[#0a0a0a] font-medium hover:opacity-60 transition-opacity order-3 hidden md:block"
          onClick={scrollToSection}
        >
          ({t('hero.scrollToExplore')})
        </div>
      </motion.div>
    </section>
  );
}
